import { getViewerTimezoneLabel } from "../../helpers/utils";

export const FIELD_APP_TIMEZONE_MODES = {
  VIEWER: "viewer",
  DATA: "data",
};

export const FIELD_APP_DATA_TIMEZONE = "America/Sao_Paulo";

/**
 * Fusos oficiais do Brasil por estado. A coluna time_zone da conta costuma
 * ficar em America/Sao_Paulo mesmo quando a cidade e o estado estão em outro
 * fuso (por exemplo Rondônia). O relógio da fazenda segue o estado cadastrado.
 */
const BRAZIL_STATE_TIMEZONES = [
  { keys: ["acre", "ac"], zone: "America/Rio_Branco" },
  { keys: ["amazonas", "am", "roraima", "rr"], zone: "America/Manaus" },
  { keys: ["rondonia", "ro"], zone: "America/Porto_Velho" },
  { keys: ["mato grosso do sul", "ms"], zone: "America/Campo_Grande" },
  { keys: ["mato grosso", "mt"], zone: "America/Cuiaba" },
];

const ZONE_PLACE_LABELS = {
  "America/Porto_Velho": "Porto Velho",
  "America/Sao_Paulo": "São Paulo",
  "America/Manaus": "Manaus",
  "America/Cuiaba": "Cuiabá",
  "America/Campo_Grande": "Campo Grande",
  "America/Rio_Branco": "Rio Branco",
  "America/Boa_Vista": "Boa Vista",
  "America/Belem": "Belém",
  "America/Fortaleza": "Fortaleza",
  "America/Recife": "Recife",
  "America/Bahia": "Salvador",
  "America/Noronha": "Fernando de Noronha",
};

function foldPlace(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/** IANA a partir do estado (ou UF) cadastrado. Null se o estado não vier. */
export function timezoneFromBrazilState(estado) {
  const state = foldPlace(estado);
  if (!state) return null;
  const match = BRAZIL_STATE_TIMEZONES.find((entry) =>
    entry.keys.includes(state)
  );
  return match ? match.zone : FIELD_APP_DATA_TIMEZONE;
}

export function friendlyTimeZoneName(zone) {
  if (!zone) return "";
  if (ZONE_PLACE_LABELS[zone]) return ZONE_PLACE_LABELS[zone];
  const city = String(zone).split("/").pop();
  return city ? city.replace(/_/g, " ") : String(zone);
}

/**
 * Relógio da fazenda: estado cadastrado na conta.
 * time_zone só entra quando o estado não veio no perfil.
 */
export function fieldAppDataTimeZone() {
  try {
    const client = JSON.parse(localStorage.getItem("client") || "null");
    const fromState = timezoneFromBrazilState(client && client.estado);
    if (fromState) return fromState;
    const zone = client && String(client.time_zone || "").trim();
    if (zone && zone.includes("/")) return zone;
  } catch (_) {
    /* ignore */
  }
  return FIELD_APP_DATA_TIMEZONE;
}

const STORAGE_PREFIX = "field-app-tracking-tz-mode-";

export function readTrackingTimezoneMode(farmId) {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${farmId || "default"}`);
    if (
      raw === FIELD_APP_TIMEZONE_MODES.VIEWER ||
      raw === FIELD_APP_TIMEZONE_MODES.DATA
    ) {
      return raw;
    }
  } catch (error) {
    /* ignore */
  }
  return FIELD_APP_TIMEZONE_MODES.VIEWER;
}

export function writeTrackingTimezoneMode(farmId, mode) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${farmId || "default"}`, mode);
  } catch (error) {
    /* ignore */
  }
}

export function getTimezoneModeShortLabel(mode) {
  if (mode === FIELD_APP_TIMEZONE_MODES.DATA) {
    return fieldAppDataTimeZone();
  }
  return getViewerTimezoneLabel();
}

export function getTimezoneModePlaceLabel(mode) {
  return friendlyTimeZoneName(getTimezoneModeShortLabel(mode));
}

function toDate(value) {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function resolveTimeZone(mode) {
  return mode === FIELD_APP_TIMEZONE_MODES.DATA
    ? fieldAppDataTimeZone()
    : undefined;
}

function formatWithIntl(date, mode, options) {
  const timeZone = resolveTimeZone(mode);
  return new Intl.DateTimeFormat("pt-BR", { timeZone, ...options }).format(
    date
  );
}

/**
 * Formata instantes para mapa, gráfico e cartões do rastreamento.
 * variant: hm | dm | dmhm | card | full | map | tooltip
 */
export function formatFieldAppDateTime(value, mode, variant = "card") {
  const date = toDate(value);
  if (!date) return "—";

  switch (variant) {
    case "hm":
      return formatWithIntl(date, mode, {
        hour: "2-digit",
        minute: "2-digit",
      });
    case "hms":
      return formatWithIntl(date, mode, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    case "dm":
      return formatWithIntl(date, mode, {
        day: "2-digit",
        month: "2-digit",
      });
    case "dmhm":
      return formatWithIntl(date, mode, {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    case "map": {
      const formatted = formatWithIntl(date, mode, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
      return formatted.replace(", ", " - ");
    }
    case "full":
    case "tooltip":
      return formatWithIntl(date, mode, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    case "card":
    default:
      return formatWithIntl(date, mode, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
  }
}
